import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { money } from "@/lib/format";
import { Smartphone, Copy, CheckCircle2, Clock, Loader2 } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";

export default function Payment() {
  const { order_number } = useParams();
  const { state } = useLocation();
  const { settings } = useAuth();
  const nav = useNavigate();
  const [order, setOrder] = useState(state?.order || null);
  const [asked, setAsked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const cashtag = (settings?.cashtag || "$OverflowPopcornCo").replace(/^\$/, "");

  useEffect(() => {
    if (!order && order_number) {
      // fallback: can't fetch without phone; keep minimal
    }
  }, [order, order_number]);

  const openCashApp = () => {
    const url = `https://cash.app/$${cashtag}/${order?.total?.toFixed(2) || ""}`;
    window.open(url, "_blank");
    setAsked(true);
  };

  const copyTag = () => {
    navigator.clipboard.writeText(`$${cashtag}`);
    toast.success("Cashtag copied!");
  };

  const iPaid = async () => {
    setSubmitting(true);
    try {
      await api.post(`/orders/${order_number}/payment-submitted`);
      toast.success("Thank you! We'll confirm your payment shortly.");
      nav(`/confirmation/${order_number}`, { state: { order: { ...order, payment_status: "payment_submitted" } } });
    } catch {
      nav(`/confirmation/${order_number}`, { state: { order } });
    } finally {
      setSubmitting(false);
    }
  };

  const payLater = () => nav(`/confirmation/${order_number}`, { state: { order } });

  if (!order) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center">
        <h1 className="font-serif text-3xl text-cream mb-4">Payment</h1>
        <p className="text-cream/60 mb-6">We couldn't load your order details. You can track your order any time.</p>
        <Button className="btn-gold rounded-full px-8 py-6" onClick={() => nav("/track")}>Track Order</Button>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-12">
      <div className="gold-border-card rounded-2xl bg-card-surface p-8 text-center" data-testid="cashapp-payment-panel">
        <div className="inline-flex items-center gap-2 bg-[#00D632]/15 text-[#00D632] px-4 py-1.5 rounded-full text-sm font-semibold mb-5">
          <Smartphone size={16} /> Pay with Cash App
        </div>
        <p className="text-cream/60 text-sm">Order Total</p>
        <p className="font-mono text-gold text-5xl font-bold my-2" data-testid="payment-total">{money(order.total)}</p>
        <p className="text-cream/60 text-sm">Order Number</p>
        <p className="font-mono text-cream text-2xl font-semibold mb-6" data-testid="payment-order-number">#{order.order_number}</p>

        <div className="bg-obsidian/60 rounded-xl border border-gold/20 p-4 mb-6 flex flex-col items-center">
          <div className="bg-white p-3 rounded-xl mb-3" data-testid="cashapp-qr">
            <QRCodeCanvas value={`https://cash.app/$${cashtag}/${order.total?.toFixed(2) || ""}`} size={148} bgColor="#ffffff" fgColor="#00A82D" level="M" />
          </div>
          <p className="text-cream/60 text-xs mb-1">Scan to pay, or send to</p>
          <button onClick={copyTag} className="flex items-center justify-center gap-2 mx-auto font-mono text-gold text-xl font-bold" data-testid="cashtag-display">
            ${cashtag} <Copy size={16} className="text-cream/50" />
          </button>
        </div>

        <div className="bg-burgundy/30 rounded-xl border border-gold/20 p-4 mb-6 text-left">
          <p className="text-cream/80 text-sm">📝 Please include your order number in the Cash App payment note.</p>
          <p className="font-mono text-gold text-sm mt-1">Example: Order #{order.order_number}</p>
        </div>

        <Button onClick={openCashApp} className="w-full rounded-full py-6 text-base font-bold bg-[#00D632] hover:brightness-110 text-black mb-4" data-testid="pay-with-cashapp-btn">
          Pay with Cash App
        </Button>

        {asked && (
          <div className="border-t border-gold/20 pt-6 mt-2" data-testid="did-you-pay-panel">
            <p className="text-cream font-serif text-lg mb-4">Did you complete your payment?</p>
            <div className="flex gap-3">
              <Button onClick={iPaid} disabled={submitting} className="btn-gold flex-1 rounded-full py-5 font-semibold" data-testid="cashapp-confirm-payment-btn">
                {submitting ? <Loader2 className="animate-spin" size={16} /> : <span className="flex items-center gap-1"><CheckCircle2 size={16} /> Yes, I Paid</span>}
              </Button>
              <Button onClick={payLater} variant="outline" className="flex-1 rounded-full py-5 border-gold/40 text-cream bg-transparent hover:bg-gold/10" data-testid="pay-later-btn">
                <Clock size={16} className="mr-1" /> I'll Pay Later
              </Button>
            </div>
          </div>
        )}

        <p className="text-cream/40 text-xs mt-6">
          Your order stays <span className="text-gold">Awaiting Payment</span> until the owner confirms it was received. We never store Cash App credentials.
        </p>
      </div>
    </div>
  );
}
